import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridRowEditStopReasons
} from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import {Alert, Card} from "@mui/material";
import {GET, PATCH, DELETE} from "APIclient";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {useLocation} from "react-router-dom";
import {formatDate} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

export default function Experience() {
  const [data, setData] = React.useState([]);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const location = useLocation();
  const empId = location.state || window.sessionStorage.getItem("empId");
  const fetchUrl = `employee/employee-details/${empId}`;
  const updateUrl = `employee/experience`;
  const [open, setOpen] = React.useState(false);
  const [newId, setNewId] = React.useState(1);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");
  const [snackbar, setSnackbar] = React.useState(null);
  const [hasExperienceDetailsPermission, setHasExperienceDetailsPermission] = React.useState(false);

  const handleCloseSnackbar = () => setSnackbar(null);

  const handleClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await GET(fetchUrl);
        const data = response?.data?.data;
        const experiences = data?.experiences || [];

        // Log rows with invalid employeeExperienceId
        experiences.forEach(row => {
          if (!row.employeeExperienceId) {
            console.warn("Row with missing employeeExperienceId:", row);
          }
        });

        setData(experiences);
        setHasExperienceDetailsPermission(data?.employee.permissions.includes("experience"));
        const maxId =
          experiences.length > 0
            ? Math.max(...experiences.map(row => row.employeeExperienceId || 0))
            : 0;
        setNewId(maxId + 1);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [fetchUrl]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = id => () => {
    if (!hasExperienceDetailsPermission) return;
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
  };

  const handleSaveClick = id => () => {
    if (!hasExperienceDetailsPermission) return;
    setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
  };

  const handleDeleteClick = id => async () => {
    if (!hasExperienceDetailsPermission) return;

    // Check if the row exists in the data
    const rowExists = data.find(row => row.employeeExperienceId === id);
    if (!rowExists) {
      setSnakColor("error");
      setSnakMessage("Row not found");
      setOpen(true);
      return;
    }

    try {
      const deleteUrl = `employee/delete-experience/${id}`;
      const DeleteRes = await DELETE(deleteUrl);
      if (DeleteRes.status === 200) {
        // Update data state and ensure the row is removed
        setData(prevData => prevData.filter(row => row.employeeExperienceId !== id));
        setSnakColor("success");
        setSnakMessage("Experience deleted successfully");
        setOpen(true);

        // Refetch data to ensure consistency
        const response = await GET(fetchUrl);
        const newData = response?.data?.data;
        setData(newData?.experiences || []);
        const maxId =
          newData?.experiences?.length > 0
            ? Math.max(...newData.experiences.map(row => row.employeeExperienceId))
            : 0;
        setNewId(maxId + 1);
      } else if (DeleteRes.statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Deletion failed");
        setOpen(true);
      }
    } catch (error) {
      console.error("Error deleting row:", error);
      setSnakColor("error");
      setSnakMessage("Failed to delete experience");
      setOpen(true);
    }
  };

  const handleCancelClick = id => () => {
    if (!hasExperienceDetailsPermission) return;
    setRowModesModel({
      ...rowModesModel,
      [id]: {mode: GridRowModes.View, ignoreModifications: true}
    });

    const editedRow = data.find(row => row.employeeExperienceId === id);
    if (editedRow.isNew) {
      setData(data.filter(row => row.employeeExperienceId !== id));
      const maxId =
        data.length > 1
          ? Math.max(
              ...data
                .filter(row => row.employeeExperienceId !== id)
                .map(row => row.employeeExperienceId)
            )
          : 0;
      setNewId(maxId + 1);
    }
  };

  const processRowUpdate = newRow => {
    if (!hasExperienceDetailsPermission) return newRow;
    const updatedRow = {...newRow, isNew: false};
    setData(data =>
      data.map(row =>
        row.employeeExperienceId === updatedRow.employeeExperienceId ? updatedRow : row
      )
    );

    try {
      PATCH(updateUrl, {empId: empId, experiences: [updatedRow]})
        .then(response => {
          response = response.data;
          if (response?.statusCode === 200) {
            console.log("Update successful");
            setSnakColor("success");
            setSnakMessage("Experience updated successfully");
            setOpen(true);
            GET(fetchUrl).then(responseDt => {
              const data = responseDt?.data?.data;
              setData(data?.experiences || []);
              const maxId =
                data?.experiences?.length > 0
                  ? Math.max(...data.experiences.map(row => row.employeeExperienceId))
                  : 0;
              setNewId(maxId + 1);
            });
          }
        })
        .catch(error => {
          const err = error?.response;
          if (err.data?.statusCode == 400 || err.data?.statusCode === 409) {
            setSnakColor("error");
            setSnakMessage(err.data?.message);
            setOpen(true);
          } else if (err.data?.statusCode == 406) {
            setSnakColor("error");
            setSnakMessage(err.data?.message);
            setOpen(true);
          } else if (err.data?.statusCode == 403) {
            setSnakColor("error");
            setSnakMessage(err.data?.message);
            setOpen(true);
          } else {
            setSnakColor("error");
            setSnakMessage("Failed to update experience");
            setOpen(true);
          }
        });
    } catch (error) {
      console.error("Error updating row:", error);
    }
    return updatedRow;
  };

  const handleProcessRowUpdateError = React.useCallback(error => {
    setSnackbar({children: error.message, severity: "error"});
  }, []);

  const handleRowModesModelChange = newRowModesModel => {
    setRowModesModel(newRowModesModel);
  };

  const columns = [
    {field: "employeeExperienceId", headerName: "ID", hide: true},
    {
      field: "companyName",
      headerName: "Company Name",
      width: 150,
      editable: hasExperienceDetailsPermission
    },
    {
      field: "positionName",
      headerName: "Position",
      width: 150,
      align: "left",
      headerAlign: "left",
      editable: hasExperienceDetailsPermission
    },
    {
      field: "location",
      headerName: "Location",
      width: 100,
      editable: hasExperienceDetailsPermission,
      type: "singleSelect",
      valueOptions: ["Pune", "Mumbai", "Nashik", "Delhi"]
    },
    {
      field: "joinDate",
      headerName: "Start Date",
      width: 150,
      renderCell: params => formatDate(params.value),
      renderEditCell: params => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD/MM/YYYY"
            value={params.value ? dayjs(params.value) : null}
            onChange={date => {
              const formattedDate = date ? date.format("YYYY-MM-DD") : null;
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: formattedDate
              });
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                fullWidth: true,
                size: "medium",
                sx: {
                  "& .MuiOutlinedInput-root": {
                    padding: "4px 8px",
                    height: "50px",
                    fontSize: "14px"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0"
                  }
                }
              }
            }}
            disabled={!hasExperienceDetailsPermission}
          />
        </LocalizationProvider>
      ),
      editable: hasExperienceDetailsPermission
    },
    {
      field: "leaveDate",
      headerName: "End Date",
      width: 150,
      renderCell: params => formatDate(params.value),
      renderEditCell: params => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            format="DD/MM/YYYY"
            value={params.value ? dayjs(params.value) : null}
            onChange={date => {
              const formattedDate = date ? date.format("YYYY-MM-DD") : null;
              params.api.setEditCellValue({
                id: params.id,
                field: params.field,
                value: formattedDate
              });
            }}
            slotProps={{
              textField: {
                variant: "outlined",
                fullWidth: true,
                size: "medium",
                sx: {
                  "& .MuiOutlinedInput-root": {
                    padding: "4px 8px",
                    height: "50px",
                    fontSize: "14px"
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0"
                  }
                }
              }
            }}
            disabled={!hasExperienceDetailsPermission}
          />
        </LocalizationProvider>
      ),
      editable: hasExperienceDetailsPermission
    },
    {
      field: "document",
      headerName: "File",
      width: 150,
      editable: hasExperienceDetailsPermission,
      renderCell: params => {
        const handleViewFile = async () => {
          const base64Data = params.value;
          if (base64Data) {
            try {
              const byteCharacters = atob(base64Data);
              const byteNumbers = Array.from(byteCharacters, char => char.charCodeAt(0));
              const byteArray = new Uint8Array(byteNumbers);
              const blob = new Blob([byteArray], {type: "application/pdf"});
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, "_blank");
              setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
            } catch (error) {
              console.error("Error viewing file:", error);
              setSnakColor("error");
              setSnakMessage("Failed to view document");
              setOpen(true);
            }
          }
        };

        return params.value && params.value.length ? (
          <Button
            size="medium"
            startIcon={<VisibilityIcon />}
            onClick={handleViewFile}
            disabled={!hasExperienceDetailsPermission}
          >
            View
          </Button>
        ) : (
          <span>No File</span>
        );
      },
      renderEditCell: params => {
        const handleFileChange = event => {
          const file = event.target.files[0];
          if (file) {
            let fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
              if (fileReader.result) {
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: fileReader.result.split(",")[1]
                });
              }
            };
            fileReader.onerror = () => {
              setSnakColor("error");
              setSnakMessage("Failed to read file");
              setOpen(true);
            };
          }
        };

        return (
          <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
            <label htmlFor={`file-upload-${params.id}`} style={{display: "inline-block"}}>
              <input
                id={`file-upload-${params.id}`}
                type="file"
                accept="application/pdf"
                style={{display: "none"}}
                onChange={handleFileChange}
                disabled={!hasExperienceDetailsPermission}
              />
              <Button
                variant="contained"
                size="small"
                style={{color: "#fff"}}
                component="span"
                startIcon={<AttachFileIcon />}
                disabled={!hasExperienceDetailsPermission}
              >
                {params.value && params.value.length ? "Replace" : "Upload"}
              </Button>
            </label>
          </div>
        );
      }
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: ({id}) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{color: "primary.main"}}
              onClick={handleSaveClick(id)}
              disabled={!hasExperienceDetailsPermission}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
              disabled={!hasExperienceDetailsPermission}
            />
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(id)}
            color="inherit"
            disabled={!hasExperienceDetailsPermission}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(id)}
            color="inherit"
            disabled={!hasExperienceDetailsPermission}
          />
        ];
      }
    }
  ];

  // Modified to show all rows, regardless of status
  const activeRows = data && data.length > 0 ? data : [];

  return (
    <MDBox px={2} pt={4} pb={3}>
      <Box
        sx={{
          height: 500,
          width: "100%",
          "& .actions": {
            color: "text.secondary"
          },
          "& .textPrimary": {
            color: "text.primary"
          }
        }}
      >
        <DataGrid
          rows={activeRows}
          columns={columns}
          editMode="row"
          rowModesModel={rowModesModel}
          getRowId={row => row.employeeExperienceId}
          columnVisibilityModel={{
            employeeExperienceId: false
          }}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          slots={{
            toolbar: EditToolbar
          }}
          slotProps={{
            toolbar: {data, setRowModesModel, newId, setNewId, hasExperienceDetailsPermission}
          }}
          initialState={{
            sorting: {
              sortModel: []
            }
          }}
          sx={{
            fontFamily: properties.fontFamily,
            fontSize: "14px",
            "& .MuiDataGrid-editInputCell": {
              border: "1px solid #e0e0e0",
              padding: "4px",
              backgroundColor: "#fff",
              borderRadius: "0px",
              "&:focus-within": {
                borderColor: "#e0e0e0",
                outline: "none",
                boxShadow: "none"
              }
            },
            "& .MuiInputBase-root": {
              borderRadius: "0px !important",
              boxShadow: "none !important"
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "1px solid #e0e0e0 !important"
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e0e0"
            }
          }}
        />
        <Snackbar
          open={open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snakColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </Box>
    </MDBox>
  );

  function EditToolbar(props) {
    const {data, setRowModesModel, newId, setNewId, hasExperienceDetailsPermission} = props;

    const handleClick = () => {
      if (!hasExperienceDetailsPermission) return;
      const newRow = {
        employeeExperienceId: newId,
        companyName: "",
        positionName: "",
        location: "",
        startDate: null,
        endDate: null,
        document: null,
        status: 1,
        isNew: true
      };
      setData(prevRows => [...(prevRows || []), newRow]);
      setRowModesModel(oldModel => ({
        ...oldModel,
        [newId]: {mode: GridRowModes.Edit, fieldToFocus: "companyName"}
      }));
      setNewId(prevId => prevId + 1);
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleClick}
          disabled={!hasExperienceDetailsPermission}
        >
          Add record
        </Button>
      </GridToolbarContainer>
    );
  }
}
